import { useCallback, useEffect, useState } from 'react'

import { supabase } from '../../utils/helpers/supabase.helpers'
import {
  GEOXP_AUDIO_BUCKET,
  GEOXP_VIDEO_BUCKET,
  SPOT_LOCALIZATIONS_TABLE,
} from '../../utils/constants/db.constants'
import { SIGNED_URL_VALIDITY } from '../../utils/constants/supabase.constants'
import { cleanFileName } from '../../utils/helpers/upload.helpers'
import type { LocalizationRemoval, LocalizationUpsert } from '../../utils/helpers/upload-v2.helpers'

export type LocalizationDraft = {
  locale: string
  description: string
  /** New file picked in this session, not yet uploaded. */
  mediaFile: File | null
  /** What the database row carries, when the localization already exists. */
  existing?: {
    audio_content_path: string | null
    audio_filename: string | null
    /** Ephemeral signed URL used only by the private editor preview. */
    mediaUrl?: string
  }
}

/** Description OR media is enough to show that the author started this localization. */
export const localizationHasContent = (draft: LocalizationDraft): boolean =>
  Boolean(draft.description.trim() || draft.mediaFile || draft.existing?.audio_content_path)

/** A newly selected file or an already persisted path satisfies required-media validation. */
export const localizationHasMedia = (draft: LocalizationDraft): boolean =>
  Boolean(draft.mediaFile || draft.existing?.audio_content_path)

/**
 * State and save-diff of an agami's ADDITIONAL languages (08.2). The main
 * language never lives here: it is the spot's base columns, owned by the form —
 * which is what keeps a monolingual agami identical to what the v1 produces.
 *
 * The contract is "given an agami, manage its localizations": nothing about
 * Onde, nothing about words. The read goes through RLS as the owner — the only
 * client this editor serves (08.1's policy).
 */
export const useLocalizations = ({
  agamiId,
  enabled,
  contentMediaType,
}: {
  agamiId?: string
  enabled: boolean
  contentMediaType: number
}) => {
  const [drafts, setDrafts] = useState<LocalizationDraft[]>([])
  const [removals, setRemovals] = useState<LocalizationRemoval[]>([])
  const [isLoading, setIsLoading] = useState(Boolean(enabled && agamiId))

  const load = useCallback(async () => {
    if (!enabled || !agamiId) return
    setIsLoading(true)

    const { data } = await supabase
      .from(SPOT_LOCALIZATIONS_TABLE)
      .select('locale, description, audio_content_path, audio_filename')
      .eq('spot_id', agamiId)
      .order('locale')

    const rows = data ?? []
    const mediaPaths = rows.flatMap((row) => (row.audio_content_path ? [row.audio_content_path] : []))
    const bucket = contentMediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET
    const { data: signedUrls } =
      mediaPaths.length > 0
        ? await supabase.storage.from(bucket).createSignedUrls(mediaPaths, SIGNED_URL_VALIDITY)
        : { data: [] }
    const mediaUrlsByPath = new Map(
      (signedUrls ?? []).flatMap((item) => (item.signedUrl ? [[item.path, item.signedUrl] as const] : []))
    )

    setDrafts(
      rows.map((row) => ({
        locale: row.locale,
        description: row.description ?? '',
        mediaFile: null,
        existing: {
          audio_content_path: row.audio_content_path,
          audio_filename: row.audio_filename,
          mediaUrl: row.audio_content_path ? mediaUrlsByPath.get(row.audio_content_path) : undefined,
        },
      }))
    )
    setRemovals([])
    setIsLoading(false)
  }, [agamiId, enabled, contentMediaType])

  useEffect(() => {
    load()
  }, [load])

  const addLanguage = useCallback((locale: string) => {
    setDrafts((current) =>
      current.some((d) => d.locale === locale)
        ? current
        : [...current, { locale, description: '', mediaFile: null }]
    )
    // Re-adding a language that was marked for removal cancels the removal.
    setRemovals((current) => current.filter((r) => r.locale !== locale))
  }, [])

  const removeLanguage = useCallback((locale: string) => {
    setDrafts((current) => {
      const draft = current.find((d) => d.locale === locale)
      // Only languages that exist in the database need a removal entry: a tab
      // added and removed in the same session was never saved.
      if (draft?.existing) {
        setRemovals((prev) => [
          ...prev.filter((r) => r.locale !== locale),
          { locale, audioContentPath: draft.existing?.audio_content_path },
        ])
      }
      return current.filter((d) => d.locale !== locale)
    })
  }, [])

  const setDescription = useCallback((locale: string, description: string) => {
    setDrafts((current) => current.map((d) => (d.locale === locale ? { ...d, description } : d)))
  }, [])

  const setMediaFile = useCallback((locale: string, mediaFile: File | null) => {
    setDrafts((current) => current.map((d) => (d.locale === locale ? { ...d, mediaFile } : d)))
  }, [])

  const clearMediaFiles = useCallback(() => {
    setDrafts((current) => current.map((draft) => ({ ...draft, mediaFile: null })))
  }, [])

  /**
   * What the save orchestrator needs (upsertAgamiSpotV2): every present tab as
   * an idempotent upsert, plus the removals. Empty tabs are saved too — an
   * empty row can exist in local state, but author-mode validation prevents it
   * from being persisted when the selected media type requires a file.
   */
  const toSaveInput = useCallback(
    (): { upserts: LocalizationUpsert[]; removals: LocalizationRemoval[] } => ({
      upserts: drafts.map((d) => ({
        locale: d.locale,
        description: d.description.trim() || null,
        mediaFile: d.mediaFile,
        mediaFilename: cleanFileName({ file: d.mediaFile }),
        existingAudioPath: d.existing?.audio_content_path ?? null,
      })),
      removals,
    }),
    [drafts, removals]
  )

  const isDirty = drafts.length > 0 || removals.length > 0

  return {
    drafts,
    isLoading,
    isDirty,
    addLanguage,
    removeLanguage,
    setDescription,
    setMediaFile,
    clearMediaFiles,
    hasPersistedMedia: drafts.some((draft) => Boolean(draft.existing?.audio_content_path)),
    toSaveInput,
    /** After a successful save the local diff is stale: reload from the rows. */
    reload: load,
  }
}
